import { useEffect, useState } from 'react';
import { Button, Card, CardContent, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { fetchAdmin, postAdmin, toErrorMessage } from '../../admin/api';
import {
  AdminConfirmationDialog,
  AdminDataTable,
} from '../../sections/admin/shared';
import { AdminRouteLayout, RequestState, useAsyncState } from './AdminRoutePageShared';

export default function AccessControlRequestsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const requests = useAsyncState([]);
  const permissions = useAsyncState([]);
  const [requestForm, setRequestForm] = useState({
    staff_id: '',
    permission_code: '',
    resource_type: '',
    resource_id: '',
    reason: '',
  });
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    const load = async () => {
      requests.setLoading();
      permissions.setLoading();
      try {
        const [requestResponse, permissionResponse] = await Promise.all([
          fetchAdmin('/access/requests'),
          fetchAdmin('/access/permissions'),
        ]);
        requests.setData(requestResponse.requests || []);
        permissions.setData(permissionResponse.permissions || []);
      } catch (error) {
        const message = toErrorMessage(error);
        requests.setError(message);
        permissions.setError(message);
      }
    };

    load();
  }, []);

  const createAccessRequest = async () => {
    try {
      await postAdmin('/access/requests', requestForm);
      enqueueSnackbar('Access request created', { variant: 'success' });
      setRequestForm({ staff_id: '', permission_code: '', resource_type: '', resource_id: '', reason: '' });
      const refreshed = await fetchAdmin('/access/requests');
      requests.setData(refreshed.requests || []);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    }
  };

  const completeAction = async () => {
    try {
      await postAdmin(pendingAction.path);
      enqueueSnackbar(pendingAction.success, { variant: 'success' });
      const refreshed = await fetchAdmin('/access/requests');
      requests.setData(refreshed.requests || []);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <AdminRouteLayout title="Requests" breadcrumbTitle="Access Control">
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <RequestState loading={requests.state.loading} error={requests.state.error}>
            <AdminDataTable
              title="Access Requests"
              description="Pending and reviewed elevated-access requests."
              columns={[
                { field: 'staffId', headerName: 'Staff ID' },
                { field: 'permission', headerName: 'Permission' },
                { field: 'status', headerName: 'Status' },
                { field: 'resource', headerName: 'Resource' },
                {
                  field: 'actions',
                  headerName: 'Actions',
                  render: (row) => (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => setPendingAction({ path: `/access/requests/${row.id}/approve`, success: 'Request approved' })}>
                        Approve
                      </Button>
                      <Button size="small" color="error" onClick={() => setPendingAction({ path: `/access/requests/${row.id}/reject`, success: 'Request rejected' })}>
                        Reject
                      </Button>
                    </Stack>
                  ),
                },
              ]}
              rows={(requests.state.data || []).map((row) => ({
                id: row.id,
                staffId: row.staff_id,
                permission: row.permission_code,
                status: row.status,
                resource: `${row.resource_type}:${row.resource_id}`,
              }))}
              emptyTitle="No access requests"
              emptyDescription="The access requests endpoint returned no request rows."
            />
          </RequestState>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">Request Access</Typography>
                <TextField label="Staff ID" value={requestForm.staff_id} onChange={(event) => setRequestForm((prev) => ({ ...prev, staff_id: event.target.value }))} />
                <TextField select label="Permission" value={requestForm.permission_code} onChange={(event) => setRequestForm((prev) => ({ ...prev, permission_code: event.target.value }))}>
                  {(permissions.state.data || []).map((permission) => (
                    <MenuItem key={permission.id} value={permission.permission_code}>
                      {permission.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField label="Resource type" value={requestForm.resource_type} onChange={(event) => setRequestForm((prev) => ({ ...prev, resource_type: event.target.value }))} />
                <TextField label="Resource ID" value={requestForm.resource_id} onChange={(event) => setRequestForm((prev) => ({ ...prev, resource_id: event.target.value }))} />
                <TextField multiline minRows={3} label="Reason" value={requestForm.reason} onChange={(event) => setRequestForm((prev) => ({ ...prev, reason: event.target.value }))} />
                <LoadingButton variant="contained" onClick={createAccessRequest}>
                  Submit Request
                </LoadingButton>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <AdminConfirmationDialog
        open={Boolean(pendingAction)}
        title="Confirm action"
        description="This action will be sent to the governed backend immediately."
        confirmLabel="Continue"
        onClose={() => setPendingAction(null)}
        onConfirm={completeAction}
      />
    </AdminRouteLayout>
  );
}
